import React, { lazy, Suspense, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, ShieldCheck, TrendingUp, Clock, Phone } from 'lucide-react';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const LiquidityDashboard = lazy(() => import('./LiquidityDashboard'));
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HeroSection() {
  const [heroPhone, setHeroPhone] = useState('');
  const [heroSent, setHeroSent] = useState(() => !!localStorage.getItem('equiguard_lead_sent'));
  const [heroLoading, setHeroLoading] = useState(false);
  const heroLoadedAt = useRef(Date.now());

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleHeroSubmit = async () => {
    if (!heroPhone.trim() || heroPhone.trim().length < 5) return;
    setHeroLoading(true);
    try {
      const eventId = 'lead_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      await axios.post(`${BACKEND_URL}/api/leads`, {
        firmenname: 'Hero-Schnellanfrage',
        telefonnummer: heroPhone,
        email: 'schnellanfrage@equiguard.de',
        nachricht: 'Schnellanfrage aus Hero-Formular',
        event_id: eventId,
        form_loaded_at: heroLoadedAt.current,
      });
      setHeroSent(true);
      localStorage.setItem('equiguard_lead_sent', 'true');
      if (window.fbq) window.fbq('track', 'Lead', { content_name: 'Hero Schnellanfrage' }, { eventID: eventId });
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error('Zu viele Anfragen. Bitte warten Sie einige Minuten.');
      } else if (e.response?.status === 409) {
        toast.info('Anfrage bereits gesendet. Wir melden uns bei Ihnen!');
        setHeroSent(true);
        localStorage.setItem('equiguard_lead_sent', 'true');
      } else {
        toast.error('Fehler. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setHeroLoading(false);
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] sm:min-h-screen flex items-center overflow-hidden bg-gradient-hero"
    >
      {/* Static decorative elements */}
      <div className="absolute top-20 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #007aff 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #007aff 0%, transparent 70%)' }} />
      <div className="absolute top-40 left-5 sm:left-20 w-2 sm:w-3 h-2 sm:h-3 rounded-full bg-[#007aff]/20 float hidden sm:block" />
      <div className="absolute top-60 right-10 sm:right-40 w-2 h-2 rounded-full bg-[#007aff]/30 float-slow hidden sm:block" />
      <div className="absolute bottom-40 left-1/3 w-3 sm:w-4 h-3 sm:h-4 rounded-full bg-[#007aff]/10 float hidden sm:block" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-12 sm:pb-20 w-full relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="fade-in-up">
            <Badge
              data-testid="hero-badge"
              className="mb-5 sm:mb-8 rounded-full px-3 sm:px-4 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold border-[#007aff]/20 bg-[#007aff]/[0.06] text-[#007aff] hover:bg-[#007aff]/10"
              variant="outline"
            >
              Professionelles Forderungsmanagement ab 0,60€/Tag
            </Badge>

            <h1
              data-testid="hero-heading"
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-3 sm:mb-4"
              style={{ color: '#0a1628' }}
            >
              Offene Forderungen?{' '}
              <span className="gradient-text">Wir holen Ihr Geld zurück.</span>
            </h1>

            {/* Price Anchor */}
            <div data-testid="hero-price" className="flex items-baseline gap-2 mb-5 sm:mb-7">
              <span className="text-2xl sm:text-3xl lg:text-4xl font-bold" style={{ color: '#007aff', fontFamily: 'Manrope' }}>ab 0,60€</span>
              <span className="text-sm sm:text-base font-medium" style={{ color: '#4b5563', fontFamily: 'Manrope' }}>pro Tag</span>
            </div>

            <p
              data-testid="hero-subheading"
              className="text-sm sm:text-base lg:text-lg leading-relaxed mb-7 sm:mb-10 max-w-xl"
              style={{ color: '#4b5563', fontFamily: 'Manrope' }}
            >
              Wir übernehmen das Risiko und den Aufwand, damit das Geld dort landet,
              wo es hingehört: Auf Ihr Konto. Rechtssicher, diskret und effizient.
            </p>

            <div className="mb-6 sm:mb-8">
              {/* Hero Inline Form - Telefonnummer */}
              {!heroSent ? (
                <div data-testid="hero-inline-form" className="flex flex-col sm:flex-row gap-2.5 max-w-lg">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      data-testid="hero-phone-input"
                      type="tel"
                      placeholder="Ihre Telefonnummer"
                      value={heroPhone}
                      onChange={(e) => setHeroPhone(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleHeroSubmit()}
                      className="h-12 sm:h-14 pl-10 pr-4 rounded-full border-gray-200 bg-white shadow-sm text-sm sm:text-base focus:border-[#007aff] focus:ring-2 focus:ring-[#007aff]/20"
                      style={{ fontFamily: 'Manrope' }}
                    />
                  </div>
                  <Button
                    data-testid="hero-cta-primary"
                    onClick={handleHeroSubmit}
                    disabled={heroLoading}
                    className="rounded-full h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base font-semibold bg-gradient-cta hover:opacity-90 transition-opacity shadow-xl shadow-emerald-500/25 whitespace-nowrap"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    {heroLoading ? 'Senden...' : 'Rückruf anfordern'}
                    <ArrowRight className="w-5 h-5 ml-1" />
                  </Button>
                </div>
              ) : (
                <div data-testid="hero-form-success" className="flex items-center gap-2 text-sm font-semibold rounded-full bg-green-50 text-green-700 px-5 py-3 max-w-lg" style={{ fontFamily: 'Manrope' }}>
                  <ShieldCheck className="w-5 h-5" />
                  Danke! Wir rufen Sie schnellstmöglich zurück.
                </div>
              )}
              <p className="text-[10px] sm:text-xs mt-2 ml-1" style={{ color: '#9ca3af', fontFamily: 'Manrope' }}>
                Kostenlos & unverbindlich. Rückmeldung in unter 24h.
              </p>

              {/* Click-to-Call on Mobile */}
              <a
                href="tel:+493991634931"
                data-testid="hero-click-to-call"
                className="sm:hidden mt-3 flex items-center justify-center gap-2 h-12 rounded-full border-2 border-[#007aff] text-[#007aff] text-sm font-semibold transition-colors hover:bg-[#007aff]/5"
                style={{ fontFamily: 'Manrope' }}
              >
                <Phone className="w-4 h-4" />
                Jetzt direkt anrufen
              </a>
            </div>

            {/* Social Proof Banner */}
            <div data-testid="hero-social-proof" className="mb-8 sm:mb-10 rounded-xl sm:rounded-2xl p-4 sm:p-5 bg-gradient-to-r from-[#007aff]/[0.04] via-[#007aff]/[0.02] to-transparent border border-[#007aff]/[0.08]">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1644268756918-16348d1bc619?w=48&h=48&fit=crop&crop=face&q=60" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1538683169688-33a8bbea0be1?w=48&h=48&fit=crop&crop=face&q=60" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <img src="https://images.unsplash.com/photo-1702875581098-36844f1910f3?w=48&h=48&fit=crop&crop=face&q=60" alt="" className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  <div className="w-8 h-8 rounded-full border-2 border-white bg-[#007aff] flex items-center justify-center text-[10px] font-bold text-white" style={{ fontFamily: 'Manrope' }}>+</div>
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#0a1628', fontFamily: 'Manrope' }}>
                    Bereits <span style={{ color: '#007aff' }}>15.000.000€+</span> für über <span style={{ color: '#007aff' }}>2.500 Unternehmen</span> eingetrieben
                  </p>
                  <p className="text-xs" style={{ color: '#6b7280', fontFamily: 'Manrope' }}>
                    98% Erfolgsquote – durchschnittlich in unter 48 Stunden aktiv
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Metrics */}
            <div className="flex flex-wrap gap-5 sm:gap-8">
              {[
                { icon: ShieldCheck, value: '98%', label: 'Erfolgsquote' },
                { icon: Clock, value: '<48h', label: 'Reaktionszeit' },
                { icon: TrendingUp, value: '15M+', label: 'Beigetrieben' },
              ].map((item, i) => (
                <div key={i} data-testid={`hero-metric-${i}`} className="flex items-center gap-2.5 sm:gap-3">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#007aff]/10 to-[#007aff]/5 flex items-center justify-center">
                    <item.icon className="w-4 sm:w-5 h-4 sm:h-5 text-[#007aff]" />
                  </div>
                  <div>
                    <div className="text-base sm:text-lg font-bold" style={{ color: '#0a1628', fontFamily: 'Manrope' }}>{item.value}</div>
                    <div className="text-[10px] sm:text-xs" style={{ color: '#6b7280', fontFamily: 'Manrope' }}>{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Visual - Liquidity Dashboard (desktop only, lazy loaded) */}
          <div className="fade-in-right delay-200 hidden lg:block">
            <Suspense fallback={<div className="w-full h-96 rounded-3xl bg-gray-50 animate-pulse" />}>
              <LiquidityDashboard />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}
